import { ITrailerStatesEnum } from 'types/graphqlTypes'
import * as yup from 'yup'
import {
  TrailerStatusTagSchema,
  ITrailerStatusTag,
  statusTagMap,
} from '../../equipment-listing/schema/TrailerSchema'

export type TFacilityAutocompleteInput = {
  id: string
  text: string
}

export type TLoadAutocompleteInput = {
  id: string
  text: string
}

export interface IAssignmentSchema {
  /**
   * Active Facility object assigned to Trailer
   * @example { id: '123', text: 'Example Facility' }
   */
  activeFacility?: TFacilityAutocompleteInput | null
  /**
   * Load object assigned to Trailer
   * @example { id: '123', text: 'Example Load' }
   */
  assignedLoad?: TLoadAutocompleteInput | null
  /**
   * Empty Trailer Arrival Date
   * @example '2022-10-11T00:00:00.000'
   */
  emptyTrailerArrivalDate?: string | null
  /**
   * Empty Trailer Arrival Time
   * @example '12:00'
   */
  emptyTrailerArrivalTime?: string
  /**
   * Empty Trailer Ready Date
   * @example '2022-10-11T00:00:00.000'
   */
  emptyTrailerReadyDate?: string | null
  /**
   * Empty Trailer Ready Time
   * @example '12:00'
   */
  emptyTrailerReadyTime?: string
  /**
   * Trailer notes
   * @example 'Example notes'
   */
  notes?: string
  /**
   * Status of trailer
   * @example { value: ITrailerStatesEnum.inactive, label: 'Inactive', style: TAG_STYLE.dark }
   */
  status: ITrailerStatusTag
}

export const AssignmentSchema = yup.object<IAssignmentSchema>({
  activeFacility: yup
    .object({
      id: yup.string(),
      text: yup.string(),
    })
    .nullable()
    .when('status.value', {
      is: value =>
        value === ITrailerStatesEnum.assigned_to_facility ||
        value === ITrailerStatesEnum.empty_at_delivery,
      then: yup
        .object({
          id: yup.string().required(),
          text: yup.string().required(),
        })
        .required('Facility is required'),
    })
    .default(null),
  assignedLoad: yup
    .object({
      id: yup.string(),
      text: yup.string(),
    })
    .nullable()
    .when('status.value', {
      is: ITrailerStatesEnum.assigned_to_shipment,
      then: yup
        .object({
          id: yup.string().required(),
          text: yup.string().required(),
        })
        .required('Load Number is required'),
    }),
  emptyTrailerArrivalDate: yup
    .string()
    .nullable()
    .when('status.value', {
      is: ITrailerStatesEnum.assigned_to_facility,
      then: yup.string().required('Date is required'),
    }),
  emptyTrailerArrivalTime: yup.string().when('status.value', {
    is: ITrailerStatesEnum.assigned_to_facility,
    then: yup.string().required('Time is required'),
  }),
  emptyTrailerReadyDate: yup
    .string()
    .nullable()
    .when('status.value', {
      is: ITrailerStatesEnum.empty_at_delivery,
      then: yup.string().required('Date is required'),
    }),
  emptyTrailerReadyTime: yup.string().when('status.value', {
    is: ITrailerStatesEnum.empty_at_delivery,
    then: yup.string().required('Time is required'),
  }),
  notes: yup.string(),
  status: TrailerStatusTagSchema.required('Status is required').default(statusTagMap.inactive),
})

export type TAssignmentSchema = yup.InferType<typeof AssignmentSchema>
